/**
 * AttachPaymentModal — Attach an unmatched payment to an existing order.
 *
 * @see AC-B4 in docs/specifications/2026-05-20-unified-payment-ledger/spec.md
 *
 * UX spec:
 *  - Displays payment info (amount, source, receivedAt).
 *  - Search input → debounced 300ms → GET /admin/billing/orders?search=&limit=10
 *  - List of results: orderId, status, amount, proformaDocumentId.
 *  - After order selection: submit → POST /admin/billing/payments/:id/attach { orderId, documentId }.
 *  - 200 idempotent → close modal (toast handled by useAttachPaymentMutation onSuccess).
 *  - 409 → friendly error message based on error code.
 *  - WCAG: aria-labelledby (visible h3), ESC capture phase, focus return via triggerRef pattern.
 *  - Spinner on submit button.
 */
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useId,
} from "react";
import {
  useOrderSearchQuery,
  useAttachPaymentMutation,
} from "../../../../hooks/useAdminProductCatalog";
import type { UnmatchedPaymentDto, OrderSearchResult } from "../../../../types/adminBilling";
import { formatDate, formatCents } from "../utils/orderFormatters";

// =============================================================================
// PROPS
// =============================================================================

export interface AttachPaymentModalProps {
  open: boolean;
  payment: UnmatchedPaymentDto;
  onClose: () => void;
  /** Ref to the trigger button — focus returned here on close (WCAG 2.4.3). */
  triggerRef?: React.RefObject<HTMLButtonElement | null>;
}

// =============================================================================
// CONSTANTS
// =============================================================================

const DEBOUNCE_MS = 300;

// =============================================================================
// HELPERS
// =============================================================================

function getFriendlyError(err: unknown): string {
  const axiosErr = err as {
    response?: { status?: number; data?: { error?: string; message?: string } };
  };
  const status = axiosErr?.response?.status;
  const code = axiosErr?.response?.data?.error;
  const msg = axiosErr?.response?.data?.message;

  if (status === 409) {
    if (code === "ALREADY_ATTACHED") return "Ta płatność jest już przypięta do zamówienia.";
    if (code === "TENANT_MISMATCH") return "Płatność i zamówienie należą do różnych tenantów.";
    if (code === "ORDER_FULFILLED") return "Zamówienie jest już zrealizowane — nie można przypiąć kolejnej płatności.";
    return msg ?? "Konflikt — nie można przypiąć płatności do tego zamówienia.";
  }
  return msg ?? "Nie udało się przypiąć płatności.";
}

// =============================================================================
// COMPONENT
// =============================================================================

export const AttachPaymentModal: React.FC<AttachPaymentModalProps> = ({
  open,
  payment,
  onClose,
  triggerRef,
}) => {
  const uid = useId();
  const titleId = `attach-payment-title-${uid}`;
  const errorId = `attach-payment-error-${uid}`;

  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedOrder, setSelectedOrder] = useState<OrderSearchResult | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const searchInputRef = useRef<HTMLInputElement>(null);
  const onCloseRef = useRef(onClose);
  const triggerRefRef = useRef(triggerRef);

  useEffect(() => {
    onCloseRef.current = onClose;
    triggerRefRef.current = triggerRef;
  }, [onClose, triggerRef]);

  // Focus return to trigger on close (WCAG 2.4.3)
  const handleClose = useCallback(() => {
    onCloseRef.current();
    requestAnimationFrame(() => {
      triggerRefRef.current?.current?.focus();
    });
  }, []);

  // Reset state on open
  useEffect(() => {
    if (open) {
      setSearchInput("");
      setDebouncedSearch("");
      setSelectedOrder(null);
      setErrorMsg(null);
      // setTimeout(0) defers focus until after the current render+commit phase
      // so the search input exists in the DOM and is not hijacked by the
      // browser's default focus on the modal container after `open` flips true
      // (focus management trick — without this React 19's concurrent renderer
      // races the focus call against the dialog mount).
      setTimeout(() => searchInputRef.current?.focus(), 0);
    }
  }, [open, payment.id]);

  // Debounce search input (300ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // ESC close (capture phase — stops bubbling to parent modal)
  useEffect(() => {
    if (!open) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        handleClose();
      }
    };
    document.addEventListener("keydown", handleEscape, true);
    return () => document.removeEventListener("keydown", handleEscape, true);
  }, [open, handleClose]);

  // Focus trap
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "Tab") return;
    const modal = e.currentTarget;
    const focusable = modal.querySelectorAll<HTMLElement>(
      'button:not([disabled]), [href], input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }, []);

  const { data: searchResults, isLoading: isSearching } = useOrderSearchQuery({
    search: debouncedSearch,
    tenantId: payment.tenantId ?? undefined,
    enabled: debouncedSearch.trim().length >= 2,
  });

  const attachMutation = useAttachPaymentMutation();

  const handleSubmit = async () => {
    if (!selectedOrder) return;
    setErrorMsg(null);
    try {
      await attachMutation.mutateAsync({
        paymentId: payment.id,
        orderId: selectedOrder.id,
        documentId: selectedOrder.proformaDocumentId ?? null,
      });
      handleClose();
    } catch (err: unknown) {
      setErrorMsg(getFriendlyError(err));
    }
  };

  if (!open) return null;

  const results: OrderSearchResult[] = searchResults ?? [];
  const canSubmit = !!selectedOrder && !attachMutation.isPending;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onKeyDown={handleKeyDown}
        className="bg-white rounded-lg max-w-lg w-full mx-4 shadow-xl flex flex-col max-h-[85vh]"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between flex-shrink-0">
          <h3 id={titleId} className="text-lg font-semibold text-neutral-900">
            Przypnij płatność do zamówienia
          </h3>
          <button
            type="button"
            onClick={handleClose}
            aria-label="Zamknij"
            className="text-neutral-400 hover:text-neutral-600"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-6 py-4 space-y-4">
          {/* Payment info summary */}
          <div className="bg-neutral-50 border border-neutral-200 rounded-md p-3 text-sm space-y-1">
            <div className="flex justify-between">
              <span className="text-neutral-500">Kwota:</span>
              <span className="font-medium">{formatCents(payment.amountCents)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-neutral-500">Źródło:</span>
              <span className="font-medium">{payment.source}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-neutral-500">Data:</span>
              <span className="font-medium">{formatDate(payment.receivedAt)}</span>
            </div>
            {payment.payerEmail && (
              <div className="flex justify-between">
                <span className="text-neutral-500">Email płatnika:</span>
                <span className="font-medium">{payment.payerEmail}</span>
              </div>
            )}
          </div>

          {/* Error alert */}
          {errorMsg && (
            <div
              role="alert"
              id={errorId}
              className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-800"
            >
              {errorMsg}
            </div>
          )}

          {/* Search */}
          <div>
            <label
              htmlFor={`attach-search-${uid}`}
              className="block text-sm font-medium text-neutral-700 mb-1"
            >
              Szukaj zamówienia
            </label>
            <input
              ref={searchInputRef}
              id={`attach-search-${uid}`}
              type="text"
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
                setSelectedOrder(null);
              }}
              placeholder="Numer zamówienia lub klient..."
              aria-label="Szukaj zamówienia"
              className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {/* Search results */}
          {isSearching && (
            <div
              role="status"
              aria-label="Wyszukiwanie zamówień"
              className="flex justify-center py-4"
            >
              <span className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600" />
            </div>
          )}

          {!isSearching && debouncedSearch.trim().length >= 2 && results.length === 0 && (
            <p className="text-sm text-neutral-500 italic py-2">
              Brak wyników dla podanej frazy.
            </p>
          )}

          {results.length > 0 && (
            <ul className="divide-y divide-neutral-200 border border-neutral-200 rounded-md overflow-hidden">
              {results.map((order) => (
                <li key={order.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedOrder(order)}
                    className={`w-full text-left px-4 py-3 text-sm hover:bg-neutral-50 transition-colors ${
                      selectedOrder?.id === order.id
                        ? "bg-primary-50 border-l-4 border-primary-500"
                        : ""
                    }`}
                  >
                    <div className="flex justify-between items-start gap-2">
                      <div>
                        <span className="font-medium font-mono">{order.orderNumber}</span>
                        {order.tenantName && (
                          <span className="text-neutral-500 ml-2 text-xs">{order.tenantName}</span>
                        )}
                        <div className="text-xs text-neutral-500 mt-0.5">
                          Status: {order.status}
                          {order.proformaDocumentId && (
                            <span className="ml-2 text-green-700">• ma proformę</span>
                          )}
                        </div>
                      </div>
                      <span className="font-medium text-xs shrink-0">
                        {formatCents(order.totalGrossCents)}
                      </span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}

          {selectedOrder && (
            <div className="bg-primary-50 border border-primary-200 rounded-md p-3 text-sm">
              <span className="text-primary-800 font-medium">
                Wybrane zamówienie: {selectedOrder.orderNumber}
              </span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-neutral-200 flex gap-2 justify-end flex-shrink-0">
          <button
            type="button"
            onClick={handleClose}
            disabled={attachMutation.isPending}
            className="px-4 py-2 text-sm border border-neutral-300 rounded-md text-neutral-700 hover:bg-neutral-50 disabled:opacity-50"
          >
            Anuluj
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            title={
              !selectedOrder
                ? "Wybierz zamówienie z listy wyników, aby kontynuować"
                : attachMutation.isPending
                ? "Trwa przypinanie..."
                : undefined
            }
            aria-describedby={!selectedOrder ? `attach-submit-hint-${uid}` : undefined}
            className="px-4 py-2 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
          >
            {attachMutation.isPending && (
              <span
                className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"
                aria-hidden="true"
              />
            )}
            Przypnij
          </button>
          {!selectedOrder && (
            <span id={`attach-submit-hint-${uid}`} className="sr-only">
              Wybierz zamówienie z listy wyników, aby kontynuować
            </span>
          )}
        </div>
      </div>
    </div>
  );
};
